import DeleteIconButton from '../components/DeleteIconButton';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Chip, Collapse, Grid, IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import DraggableItem from './DraggableItem';

const CategoryItem = memo(({ category, hasWritePerm, type, moveCategory, isBespoke, excludedIds, allowedIds, onRowCategoryChange, onDeleteRow, tripId, mode, isInherited }) => {
    // Inherited categories from operator defaults are read-only in trip mode
    const isReadOnly = isInherited;

    return (
        <DraggableItem
            idx={category.idx}
            moveItem={moveCategory}
            hasWritePerm={hasWritePerm && !isReadOnly}
            type={`${type}-item`}
            sx={isInherited ? {
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
            } : {}}
        >
            <Grid container direction="row" alignItems="center" sx={{ width: '100%' }}>
                <Grid item>
                    <DragIndicatorIcon sx={{ color: isInherited ? '#bdbdbd' : 'inherit' }} />
                </Grid>
                <Grid item xs>
                    <RemoteAutocomplete
                        resource="tour_tag_category"
                        label="Tag Category"
                        variant="filled"
                        size="small"
                        disabled={isReadOnly || !category.is_new || !hasWritePerm}
                        disableClearable={true}
                        value={category.category}
                        getOptionLabel={(opt) => opt?.name || ''}
                        apiFilters={(() => {
                            const filters = [];
                            // Always apply allowedIds filter - if empty, no options will be shown
                            if (allowedIds && allowedIds.length > 0) {
                                filters.push({ field: 'id', op: 'in', value: allowedIds });
                            } else {
                                // No categories with checkbox - show nothing
                                filters.push({ field: 'id', op: 'in', value: ['0'] });
                            }
                            // Exclude already selected categories in this section
                            if (excludedIds && excludedIds.length > 0) {
                                filters.push({ field: 'id', op: 'not_in', value: excludedIds });
                            }
                            if (isBespoke) {
                                filters.push({ field: 'code', op: 'ne', value: 'departure' });
                            }
                            return filters;
                        })()}
                        textFieldProps={{ fullWidth: true }}
                        onChange={(ev, nv) => onRowCategoryChange(ev, nv, category.idx, type)}
                    />
                </Grid>
                {mode === 'trip' && (
                    <Grid item sx={{ ml: 1 }}>
                        <Chip
                            label={isInherited ? 'Inherited' : 'Trip'}
                            size="small"
                            color={isInherited ? 'warning' : 'primary'}
                            sx={{ height: 20, fontSize: '0.7rem', minWidth: 65 }}
                        />
                    </Grid>
                )}
                {!isInherited && (tripId === 'operator-defaults' || category.is_new) && (
                    <Grid item>
                        <DeleteIconButton allowed={hasWritePerm} size="medium" fontSize="medium" onClick={() => onDeleteRow(category.idx, type)} />
                    </Grid>
                )}
            </Grid>
        </DraggableItem>
    );
});

CategoryItem.displayName = 'CategoryItem';
CategoryItem.propTypes = {
    category: PropTypes.shape({
        idx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        is_new: PropTypes.bool,
        sequence: PropTypes.number,
        checkin: PropTypes.bool,
        boarding: PropTypes.bool,
        category: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
        }),
    }).isRequired,
    hasWritePerm: PropTypes.bool,
    type: PropTypes.oneOf(['checkin', 'boarding']).isRequired,
    moveCategory: PropTypes.func.isRequired,
    isBespoke: PropTypes.bool,
    excludedIds: PropTypes.arrayOf(PropTypes.string),
    allowedIds: PropTypes.arrayOf(PropTypes.string),
    onRowCategoryChange: PropTypes.func.isRequired,
    onDeleteRow: PropTypes.func.isRequired,
    tripId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mode: PropTypes.oneOf(['operator', 'trip', 'create']),
    isInherited: PropTypes.bool,
};

const CheckinAndBoardingSection = ({ trip, mode = 'operator', hasWritePerm, onChange }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    useEffect(() => {
        const needsIdx = trip.tag_categories.some(cat => !cat.idx);
        if (!needsIdx) return;
        onChange((prev) => ({
            ...prev,
            tag_categories: prev.tag_categories.map((cat, i) => ({
                ...cat,
                idx: cat.idx ?? `${cat.id || cat.category?.id || i}-${i}`,
            })),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trip.tag_categories]);

    // We no longer tie options to what is already associated; sections operate independently

    const hasSelectedCheckinCategories = useMemo(() => trip.tag_categories.some(cat => cat.checkin), [trip.tag_categories]);

    const moveCategory = useCallback((dragIdx, hoverIdx) => {
        if (dragIdx === hoverIdx) return;

        onChange((prevState) => {
            const categories = [...prevState.tag_categories];
            const draggedCategory = categories.find(cat => cat.idx === dragIdx);
            const hoverCategory = categories.find(cat => cat.idx === hoverIdx);

            if (!draggedCategory || !hoverCategory) return prevState;

            // In trip or create mode, don't allow reordering inherited categories
            if (mode === 'trip' || mode === 'create') {
                // In create mode, categories with string group_tour (like 'new-trip') are trip-level
                const isDragInherited = mode === 'create'
                    ? typeof draggedCategory.group_tour !== 'string' && typeof draggedCategory.group_tour !== 'number'
                    : typeof draggedCategory.group_tour !== 'number';
                const isHoverInherited = mode === 'create'
                    ? typeof hoverCategory.group_tour !== 'string' && typeof hoverCategory.group_tour !== 'number'
                    : typeof hoverCategory.group_tour !== 'number';
                // Don't allow dragging inherited or dropping onto inherited
                if (isDragInherited || isHoverInherited) return prevState;
            }

            // Get only trip-level categories for reordering (in trip/create mode)
            // or all categories (in operator mode)
            const reorderableCategories = (mode === 'trip' || mode === 'create')
                ? categories.filter(cat => mode === 'create'
                    ? (typeof cat.group_tour === 'string' || typeof cat.group_tour === 'number')
                    : typeof cat.group_tour === 'number')
                : categories;

            const dragIndex = reorderableCategories.findIndex(cat => cat.idx === dragIdx);
            const hoverIndex = reorderableCategories.findIndex(cat => cat.idx === hoverIdx);

            if (dragIndex === -1 || hoverIndex === -1) return prevState;
            if (dragIndex === hoverIndex) return prevState;

            // Reorder within the reorderable list
            const reordered = [...reorderableCategories];
            const [removed] = reordered.splice(dragIndex, 1);
            reordered.splice(hoverIndex, 0, removed);

            // Update sequences for reordered categories
            const sequenceMap = new Map();
            reordered.forEach((cat, index) => {
                sequenceMap.set(cat.idx, index);
            });

            const updatedCategories = categories.map(cat => {
                if (sequenceMap.has(cat.idx)) {
                    return { ...cat, sequence: sequenceMap.get(cat.idx) };
                }
                return cat;
            });

            return { ...prevState, tag_categories: updatedCategories };
        });
    }, [onChange, mode]);

    const filteredAndSortedCheckinCategories = useMemo(() => {
        const checkinCategories = trip.tag_categories.filter(cat => cat.checkin);
        if (mode === 'operator') {
            // In operator mode, just sort by sequence
            return checkinCategories.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
        }
        // In trip/create mode: inherited categories first (sorted by their sequence), then trip-level (sorted by sequence)
        const inherited = checkinCategories
            .filter(cat => mode === 'create'
                ? (typeof cat.group_tour !== 'string' && typeof cat.group_tour !== 'number')
                : typeof cat.group_tour !== 'number')
            .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
        const tripLevel = checkinCategories
            .filter(cat => mode === 'create'
                ? (typeof cat.group_tour === 'string' || typeof cat.group_tour === 'number')
                : typeof cat.group_tour === 'number')
            .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
        return [...inherited, ...tripLevel];
    }, [trip.tag_categories, mode]);

    const getExcludedIdsInSameSection = useCallback((currentRowIdx, type) => {
        return trip.tag_categories
            .filter(c => c.category?.id && c.idx !== currentRowIdx && c[type])
            .map(c => c.category.id.toString());
    }, [trip.tag_categories]);

    const getAllowedCheckinCategoryIds = useMemo(() => {
        return trip.tag_categories
            .filter(c => c.category?.id && c.checkin)
            .map(c => c.category.id.toString());
    }, [trip.tag_categories]);

    const onRowCategoryChange = useCallback((ev, selectedCategory, idx, type) => {
        if (selectedCategory !== null && !selectedCategory?.id) return;
        onChange((prevState) => {
            const items = prevState.tag_categories;

            if (selectedCategory?.id) {
                const selectedId = selectedCategory.id.toString();
                
                // Check if this category already exists with the same type flag
                const duplicateInSameSection = items.some(c => 
                    c.idx !== idx && 
                    c[type] && 
                    c.category?.id?.toString() === selectedId
                );
                if (duplicateInSameSection) return prevState;

                // Check if this category already exists in Associated (without checkin/boarding flags)
                const existingInAssociated = items.find(c => 
                    c.idx !== idx &&
                    !c.checkin && 
                    !c.boarding && 
                    c.category?.id?.toString() === selectedId
                );

                if (existingInAssociated) {
                    // Enable the flag on the existing Associated category and remove the empty row
                    const updatedItems = items
                        .filter(atc => atc.idx !== idx)
                        .map(atc => {
                            if (atc.idx === existingInAssociated.idx) {
                                return {
                                    ...atc,
                                    [type]: true,
                                };
                            }
                            return atc;
                        });
                    return { ...prevState, tag_categories: updatedItems };
                }
            }

            // Normal update if no existing category found
            const updatedItems = items.map((atc) => atc.idx === idx ? {
                ...atc,
                category: selectedCategory || null,
                [type]: selectedCategory ? true : atc[type],
            } : atc);
            return { ...prevState, tag_categories: updatedItems };
        });
    }, [onChange]);

    const onDeleteRow = useCallback((idx, type) => {
        onChange((prevState) => {
            const targetCategory = prevState.tag_categories.find(atc => atc.idx === idx);
            
            // If it's a new empty row (no category selected), remove it from the array
            if (targetCategory?.is_new && !targetCategory?.category?.id) {
                const updatedCategories = prevState.tag_categories.filter(atc => atc.idx !== idx);
                return { ...prevState, tag_categories: updatedCategories };
            }
            
            // Otherwise, just unset the flag
            const updatedCategories = prevState.tag_categories.map((atc) => {
                if (atc.idx === idx) {
                    return { ...atc, [type]: false };
                }
                return atc;
            });
            return { ...prevState, tag_categories: updatedCategories };
        });
    }, [onChange]);

    

    return (
        <Box>
            <Box
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
                <IconButton size="small" sx={{ mr: 0.5 }}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Typography fontWeight={500}>Check-in Tag Categories Order</Typography>
            </Box>

            <Collapse in={isExpanded}>
                <DndProvider backend={HTML5Backend}>
                    <Grid container direction="row" alignItems="center" justifyContent="space-between" spacing={0} sx={{ padding: '0 !important' }}>
                        {hasSelectedCheckinCategories ? (
                            <Grid item xs={12} md={8} sx={{ marginTop: '0px !important', marginBottom: '0px !important' }}>
                            {filteredAndSortedCheckinCategories.map((cat, idx) => {
                                const catWithIdx = cat.idx ? cat : { ...cat, idx: cat.id || cat.category?.id || `checkin-${idx}` };
                                // Inherited if in trip/create mode and group_tour is not appropriate type
                                const isInherited = (mode === 'trip' || mode === 'create') && (
                                    mode === 'create'
                                        ? (typeof catWithIdx.group_tour !== 'string' && typeof catWithIdx.group_tour !== 'number')
                                        : typeof catWithIdx.group_tour !== 'number'
                                );
                                return (
                                    <CategoryItem
                                        key={catWithIdx.idx || catWithIdx.id || catWithIdx.category?.id || idx}
                                        category={catWithIdx}
                                        hasWritePerm={hasWritePerm}
                                        type="checkin"
                                        moveCategory={moveCategory}
                                        isBespoke={trip.is_bespoke}
                                        excludedIds={getExcludedIdsInSameSection(catWithIdx.idx, 'checkin')}
                                        allowedIds={getAllowedCheckinCategoryIds}
                                        onRowCategoryChange={onRowCategoryChange}
                                        onDeleteRow={onDeleteRow}
                                        tripId={trip.id}
                                        mode={mode}
                                        isInherited={isInherited}
                                    />
                                );
                            })}
                            </Grid>
                        ) : (
                            <Grid item xs={12} md={8}>
                                <Typography
                                    sx={{
                                        fontSize: '90%',
                                        pl: { xs: 0, sm: '1rem' },
                                        ml: { xs: 0 },
                                        mt: '1rem',
                                        mb: { xs: '1rem', sm: 0 }
                                    }}
                                >No check-in categories selected</Typography>
                            </Grid>
                        )}
                    </Grid>
                </DndProvider>
            </Collapse>
        </Box>
    );
};

CheckinAndBoardingSection.propTypes = {
    trip: PropTypes.shape({
        tag_categories: PropTypes.arrayOf(PropTypes.shape({
            idx: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            is_new: PropTypes.bool,
            sequence: PropTypes.number,
            checkin: PropTypes.bool,
            boarding: PropTypes.bool,
            group_tour: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            category: PropTypes.shape({
                id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                name: PropTypes.string,
            }),
        })).isRequired,
        is_bespoke: PropTypes.bool,
    }).isRequired,
    mode: PropTypes.oneOf(['operator', 'trip', 'create']),
    hasWritePerm: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    isParentTouched: PropTypes.bool,
    clientErrors: PropTypes.shape({
        hasEmptyCheckinOrBoardingSelection: PropTypes.bool,
    }).isRequired,
};

export default CheckinAndBoardingSection;


